import React, { useState } from "react"
import { Box, TextField, Chip, IconButton, Paper } from "@mui/material"

import { makeStyles } from "tss-react/mui"

import Collapse from "@mui/material/Collapse"

import MobileSearchField from "./MobileSearchField"
import {
  LabelToIconsMapping,
  LabelToStateMapping,
  LabelToLoadingMapping,
  LabelToPaginatingMapping,
  LabelToCursorMapping,
  Option,
  Selected
} from "../../Types"
import { blueGrey, lightBlue } from "../../../../themes/palette"
import { ChevronDownIcon, RemoveIcon } from "../../../../icons"

interface MobileFiltersProps {
  filtersToRender: any
  enabledFilters: any
  selected: Selected
  disabledActionsFilters?: string[]

  onClear: (l_label: string) => void
  onChange: (l_label: string, option: Option) => void
  onYReachEnd: (l_label: string, text: string) => void
  onSearchTextChange: (l_label: string, text: string) => void

  options: [Option]
  // filters: any
}

const MobileFilters: React.FC<React.PropsWithChildren<MobileFiltersProps>> = ({
  filtersToRender,
  enabledFilters,
  onSearchTextChange,
  selected,
  onClear,
  onChange,
  onYReachEnd,
  options,
  disabledActionsFilters
}) => {
  const { classes, cx } = styles()

  const [expanded, setExpanded] = useState(false)
  const toggleExpanded = () => {
    setExpanded(expanded => !expanded)
  }

  const disableActions = (key: string) => disabledActionsFilters?.includes(key)

  return (
    <Box width="100%">
      {/* Filter group start */}
      <TextField
        onClick={toggleExpanded}
        label="Filters"
        variant={"outlined"}
        fullWidth={true}
        classes={{ root: classes.filtersGroupInputRoot }}
        className={cx(expanded && classes.whiteBc)}
        InputProps={{
          classes: { root: classes.inputRoot, notchedOutline: classes.notch },
          startAdornment: (
            <Box
              display="flex"
              style={{ flexWrap: "wrap" }}
              className={classes.chipsContainer}
            >
              {Object.entries(selected).map(([key, filter]: any, index) => {
                const Icon = LabelToIconsMapping[key]

                return (
                  <Chip
                    key={filter?.value}
                    classes={{ root: classes.chipRoot }}
                    size="small"
                    icon={
                      <Icon
                        style={{
                          marginRight: "0.125rem",
                          marginLeft: "0.25rem",
                          fontSize: "0.725rem",
                          color: lightBlue[300],
                          marginTop: 2
                        }}
                      />
                      // <ApartmentIcon className={"filterTypeIcon"} />
                    }
                    deleteIcon={
                      !disableActions && (
                        <IconButton disableRipple size="small">
                          <RemoveIcon
                            style={{
                              fontSize: "1rem",
                              color: "rgba(255,255,255,0.5)",
                              marginLeft: 4
                            }}
                          />
                        </IconButton>
                      )
                    }
                    label={filter?.label}
                    onDelete={
                      !disableActions &&
                      (e => {
                        e.stopPropagation()
                        onClear(key)
                        onSearchTextChange(key, "")
                      })
                    }
                    color="secondary"
                    variant="outlined"
                  />
                )
              })}
            </Box>
          ),
          endAdornment: (
            <Box display="flex">
              <IconButton disableRipple size="small">
                <ChevronDownIcon />
              </IconButton>
            </Box>
          )
        }}
      />
      {/* Filters group end */}
      <Collapse in={expanded}>
        <Paper
          classes={{
            root: classes.filtersContainer
          }}
        >
          {filtersToRender.map((item: any, index: number) => (
            <MobileSearchField
              key={item.label}
              index={index}
              cursor={options[LabelToCursorMapping[item.l_label]]}
              Icon={LabelToIconsMapping[item.l_label]}
              selected={selected[item.l_label] ?? null}
              options={options[LabelToStateMapping[item.l_label]]}
              loading={options[LabelToLoadingMapping[item.l_label]]}
              paginating={options[LabelToPaginatingMapping[item.l_label]]}
              disabled={!enabledFilters[item.l_label]}
              label={item.label}
              onChange={(option: any) => onChange(item.l_label, option)}
              onClear={() => onClear(item.l_label)}
              onYReachEnd={(text: string) => onYReachEnd(item.l_label, text)}
              onSearchTextChange={(text: string) =>
                onSearchTextChange(item.l_label, text)
              }
              disableActions={disableActions(item.l_label)}
            />
          ))}
        </Paper>
      </Collapse>
    </Box>
  )
}

const styles = makeStyles()(({ palette, zIndex, spacing, typography }) => ({
  filtersGroupInputRoot: {
    "& .MuiInputLabel-root": {
      fontWeight: 500,
      color: blueGrey[700],
      fontSize: "1rem"
    }
  },
  notch: {
    "& > legend": {
      width: 43
    }
  },
  inputRoot: {
    alignItems: "flex-start",
    padding: 12,
    "& input": {
      display: "none"
    },

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: blueGrey[200]
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: lightBlue[500]
    }
  },
  notchedOutline: {},
  chipsContainer: {
    // gap of 4px
    margin: `-${spacing(1)} 0 0 -${spacing(1)}`,
    width: `calc(100% + -${spacing(1)})`,
    "& > *": {
      margin: `${spacing(1)} 0 0 ${spacing(1)}`
    }
  },
  filtersContainer: {
    marginTop: spacing(1),
    padding: `${spacing(3)} ${spacing(5)}`,
    "& > *:not(:last-child)": {
      marginBottom: spacing(2)
    }
  },
  whiteBc: {
    backgroundColor: "#fff"
  },

  chipRoot: {
    maxWidth: 250,
    color: "#fff",
    padding: `0px ${spacing(1)}`,

    "& .MuiSvgIcon-root": {
      color: lightBlue[300]
    },

    "&:nth-of-type(1) ": {
      backgroundColor: "#0A526C !important",
      borderColor: "#0A526C"
    },
    "&:nth-of-type(2) ": {
      backgroundColor: "#0B5C7A !important",
      borderColor: "#0B5C7A"
    },
    "&:nth-of-type(3)": {
      backgroundColor: "#0C6788 !important",
      borderColor: "#0C6788"
    },
    "&:nth-of-type(4) ": {
      backgroundColor: "#0E7296 !important",
      borderColor: "#0E7296"
    },
    "&:nth-of-type(5) ": {
      backgroundColor: `${lightBlue[600]} !important`,
      borderColor: lightBlue[600]
    }
  }
}))

export default MobileFilters
